/**
 * Wholesale Quantity Rules — Minimog theme build
 * -----------------------------------------------
 * Verified against your actual theme markup (Minimog / OS 2.0):
 *   - Every quantity input (product page, sticky-add-to-cart bar, cart page,
 *     cart drawer) is a <m-quantity-input> wrapping an <input class="m-quantity__input">
 *     that carries data-quantity-variant-id="<variant id>" — this is the
 *     single source of truth we use to decide "is this a wholesale line?".
 *   - +/- buttons are <button class="m-quantity__button" name="minus|plus">.
 *   - Cart rows (page and drawer) are elements with [data-cart-item] and
 *     data-product-id="<product id>".
 *   - Collection-grid "Add to cart" / "Quick Add" buttons have NO quantity
 *     input at all — they add qty 1 directly. The network-level patch below
 *     is what enforces the minimum for those (silently — there's no field to
 *     show an inline error against).
 *
 * Requires wholesale-quantity-config.liquid to run first (defines
 * window.WholesaleQtyRules).
 *
 * IMPORTANT: this file cannot reach Shopify's checkout — no theme JS can.
 * Use a Cart & Checkout Validation app (see instructions.md) as the
 * checkout-level backstop. What THIS file can do, and now does, is stop the
 * "Buy it now" / "Add to cart" / "Check out" click on your own page before
 * it ever fires, when the quantity showing at that exact moment is invalid —
 * that's a different thing from touching checkout itself.
 */
(function () {
  'use strict';

  var RULES = window.WholesaleQtyRules || {};
  var MIN = RULES.min || 7;
  var MAX = RULES.max || 14;
  var PRODUCT_IDS = (RULES.productIds || []).map(Number);
  var VARIANT_IDS = (RULES.variantIds || []).map(Number);
  var ERROR_CLASS = 'wholesale-qty-error';

  var SELECTORS = {
    qtyInput: '.m-quantity__input',
    qtyWrapper: 'm-quantity-input, .m-quantity',
    increaseBtn: '.m-quantity__button[name="plus"]',
    decreaseBtn: '.m-quantity__button[name="minus"]',
    cartLineItem: '[data-cart-item]',
    cartContainer: '#MinimogCartDrawer, m-cart-drawer, #MinimogCart, m-cart'
  };

  function isWholesaleVariant(id) { return VARIANT_IDS.indexOf(Number(id)) !== -1; }
  function isWholesaleProduct(id) { return PRODUCT_IDS.indexOf(Number(id)) !== -1; }

  function clamp(value) {
    value = parseInt(value, 10);
    if (isNaN(value)) return MIN;
    if (value < MIN) return MIN;
    if (value > MAX) return MAX;
    return value;
  }

  function showError(input, message) {
    clearError(input);
    var msg = document.createElement('div');
    msg.className = ERROR_CLASS;
    msg.setAttribute('role', 'alert');
    msg.textContent = message;
    input.insertAdjacentElement('afterend', msg);
    input.classList.add('wholesale-qty-input--invalid');
  }

  function clearError(input) {
    input.classList.remove('wholesale-qty-input--invalid');
    var next = input.nextElementSibling;
    if (next && next.classList && next.classList.contains(ERROR_CLASS)) next.remove();
  }

  // A small, non-blocking toast for the "we stopped that click" moments —
  // Add to cart / Buy it now / Check out — as opposed to the inline error
  // message under a specific field, which is for live typing feedback.
  var toastTimer = null;
  function showToast(message) {
    var existing = document.querySelector('.wholesale-qty-toast');
    if (existing) existing.remove();
    clearTimeout(toastTimer);

    var toast = document.createElement('div');
    toast.className = 'wholesale-qty-toast';
    toast.setAttribute('role', 'alert');
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(function () { toast.classList.add('wholesale-qty-toast--visible'); });
    toastTimer = setTimeout(function () {
      toast.classList.remove('wholesale-qty-toast--visible');
      setTimeout(function () { toast.remove(); }, 300);
    }, 4500);
  }

  // The single source of truth: data-quantity-variant-id, present on every
  // quantity input in this theme regardless of context.
  function resolveIsWholesale(input) {
    var variantId = input.getAttribute('data-quantity-variant-id');
    if (variantId) return isWholesaleVariant(variantId);

    // Fallback: product page before a variant is selected, or cart rows
    // where the attribute is missing for some reason.
    var row = input.closest(SELECTORS.cartLineItem);
    if (row) {
      var pid = row.getAttribute('data-product-id');
      if (pid) return isWholesaleProduct(pid);
    }
    if (input.closest('product-form, form[action*="/cart/add"]')) {
      return !!RULES.isCurrentProductWholesale;
    }
    return false;
  }

  function bindInput(input) {
    if (input.dataset.wholesaleBound) return;
    input.dataset.wholesaleBound = 'true';

    if (!resolveIsWholesale(input)) return;

    input.setAttribute('min', MIN);
    input.setAttribute('max', MAX);

    var current = parseInt(input.value, 10);
    if (isNaN(current) || current < MIN) input.value = MIN;

    var validate = function () {
      var raw = input.value;
      var fixed = clamp(raw);
      if (String(fixed) !== String(raw)) {
        input.value = fixed;
        showError(input, 'Wholesale items require a quantity between ' + MIN + ' and ' + MAX + '.');
        // Only notify the rest of the page (other apps' listeners, theme
        // cart-total scripts) when we actually changed the value. Dispatching
        // this unconditionally — even when the value was already valid — is
        // what caused the cart-page loop: another app (Samita Wholesale)
        // listens for "change" on this same input to recompute its own
        // pricing, re-renders the input, which our MutationObserver picks up
        // as a new node, re-binds, and fires "change" again — forever.
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        clearError(input);
      }
      document.dispatchEvent(new CustomEvent('wholesale:quantity-validated', {
        detail: { input: input, value: fixed, min: MIN, max: MAX }
      }));
    };

    ['input', 'change', 'blur'].forEach(function (evt) { input.addEventListener(evt, validate); });

    var wrapper = input.closest(SELECTORS.qtyWrapper) || input.parentElement;
    if (wrapper) {
      wrapper.querySelectorAll(SELECTORS.increaseBtn + ',' + SELECTORS.decreaseBtn)
        .forEach(function (btn) { btn.addEventListener('click', function () { setTimeout(validate, 0); }); });
    }

    validate();
  }

  function scan(root) {
    (root || document).querySelectorAll(SELECTORS.qtyInput).forEach(bindInput);
  }

  // ---- Watch for cart drawers / ajax re-renders that inject new inputs ----
  // IMPORTANT: this only re-scans for newly-added quantity inputs (scan() is
  // idempotent — bindInput() no-ops on inputs it has already bound). It must
  // NEVER call scheduleAudit() here: pages like the cart page have elements
  // that mutate the DOM on their own (a live countdown timer, chat widget,
  // etc.), and every one of those mutations would otherwise re-trigger an
  // audit — an endless "keeps updating the cart" loop that has nothing to do
  // with an actual cart change. Audits are scheduled ONLY from real
  // cart-mutating fetches, in the wrapper below.
  var scanDebounce = null;
  var observer = new MutationObserver(function (mutations) {
    var needsScan = mutations.some(function (m) { return m.addedNodes && m.addedNodes.length; });
    if (!needsScan) return;
    clearTimeout(scanDebounce);
    scanDebounce = setTimeout(function () { scan(document); }, 50);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // ---- Network-level backstop ----
  // Covers collection-grid "Add to cart" / "Quick Add" buttons, which have
  // NO quantity input at all and add qty 1 directly via /cart/add.js.
  //
  // IMPORTANT: this wrapper only patches the actual cart-mutating endpoints,
  // and every internal fetch this script makes (the audit's /cart.js read,
  // its corrective /cart/change.js writes) goes through `originalFetch`
  // directly so it never re-enters this wrapper. Routing an internal call
  // back through the patched `window.fetch` is what causes an endless
  // audit -> fetch -> audit loop — that was the bug that kept "updating
  // the cart" on the cart page/drawer.
  var originalFetch = window.fetch;
  var CART_MUTATION_RE = /\/cart\/(add|change|update)\.js(\?|$)/;

  window.fetch = function (resource, init) {
    var url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
    var isCartMutation = CART_MUTATION_RE.test(url);

    if (isCartMutation && init && init.body) {
      try {
        var body = JSON.parse(init.body);
        if (/\/cart\/add\.js/.test(url)) {
          if (Array.isArray(body.items)) {
            body.items.forEach(function (item) {
              if (isWholesaleVariant(item.id)) item.quantity = clamp(item.quantity);
            });
          } else if (body.id !== undefined && body.quantity !== undefined && isWholesaleVariant(body.id)) {
            body.quantity = clamp(body.quantity);
          }
        } else if (/\/cart\/change\.js/.test(url)) {
          if (body.id !== undefined && body.quantity !== undefined && isWholesaleVariant(body.id)) {
            body.quantity = clamp(body.quantity);
          }
        }
        init.body = JSON.stringify(body);
      } catch (e) { /* never block a request just because we failed to parse it */ }
    }

    var result = originalFetch.apply(this, arguments);
    // Only schedule an audit off requests that actually mutate the cart —
    // not every fetch on the page, and never off the audit's own reads.
    if (isCartMutation) {
      result.then(function () { scheduleAudit(); }).catch(function () {});
    }
    return result;
  };

  // ---- Self-healing audit: source of truth is always /cart.js ----
  // Catches anything that slips past the UI or the fetch patch above
  // (e.g. /cart/update.js's array-style updates[], other apps, back/forward).
  var auditTimer = null;
  var auditInFlight = false;

  function scheduleAudit() {
    clearTimeout(auditTimer);
    auditTimer = setTimeout(auditCart, 400);
  }

  function auditCart() {
    if (auditInFlight) return;
    auditInFlight = true;
    originalFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var corrections = [];
        (cart.items || []).forEach(function (item) {
          if (isWholesaleVariant(item.variant_id) || isWholesaleProduct(item.product_id)) {
            var fixed = clamp(item.quantity);
            if (fixed !== item.quantity) corrections.push({ line: item.key, quantity: fixed });
          }
        });
        if (corrections.length) return applyCorrections(corrections);
      })
      .catch(function () {})
      .then(function () { auditInFlight = false; });
  }

  function applyCorrections(corrections) {
    var chain = Promise.resolve();
    corrections.forEach(function (c) {
      chain = chain.then(function () {
        return originalFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: c.line, quantity: c.quantity })
        });
      });
    });
    return chain.then(refreshCartUi);
  }

  function refreshCartUi() {
    // Minimog's own <m-cart> / <m-cart-drawer> web components listen for
    // these events after ajax cart changes; harmless if unused.
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.querySelectorAll(SELECTORS.cartContainer).forEach(function (el) {
      if (typeof el.refresh === 'function') { try { el.refresh(); } catch (e) {} }
      if (typeof el.renderContents === 'function') { try { el.renderContents(); } catch (e) {} }
    });
    scan(document);
  }

  // ================================================================
  // Add to cart / Buy it now / Check out guard
  // ----------------------------------------------------------------
  // This is the fix for a customer reaching checkout with only 1 item.
  // "Buy it now" is Shopify's own <shopify-accelerated-checkout> component —
  // it can go straight to checkout without ever calling /cart/add.js in a
  // way our network patch above can see, and it can fire before this
  // script's on-load binding has finished running. So instead of relying on
  // prior validation, we re-check the LIVE value of the relevant quantity
  // field at the exact moment "Add to cart" / "Buy it now" / "Check out" is
  // clicked, and only let the click through if it's already valid.
  //
  // Mechanism: a capture-phase listener on `document` runs BEFORE the
  // button's own listener (registered later, in the normal bubble/target
  // phase) gets a chance to. If the quantity is invalid, we call
  // stopImmediatePropagation() — the event never reaches the button's own
  // handler at all, so nothing navigates anywhere. If it's already valid,
  // we do nothing and the click proceeds completely normally.
  // ================================================================

  function findRelevantQuantityInput(el) {
    var scope = el.closest('product-info, .m-main-product, [data-cart-item], m-cart, m-cart-drawer, #MinimogCart, #MinimogCartDrawer') || document;
    return scope.querySelector(SELECTORS.qtyInput);
  }

  // Checks one specific quantity input at the moment of a click/submit.
  // Returns true if it's fine to proceed, false if we blocked it.
  function guardQuantity(input, evt) {
    if (!input || !resolveIsWholesale(input)) return true;

    var raw = input.value;
    var fixed = clamp(raw);
    if (String(fixed) === String(raw)) return true;

    evt.preventDefault();
    if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
    input.value = fixed;
    showError(input, 'Wholesale items require a quantity between ' + MIN + ' and ' + MAX + '.');
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    try { input.focus({ preventScroll: true }); } catch (e) {}
    showToast('This is a wholesale item — quantity must be between ' + MIN + ' and ' + MAX + '. It\u2019s been set to ' + fixed + '; please try again.');
    return false;
  }

  // Every wholesale line currently visible on the page, checked at once —
  // used right before "Check out" from the cart page or cart drawer, where
  // there can be more than one line item.
  function guardAllVisibleWholesaleLines(evt) {
    var blocked = false;
    document.querySelectorAll(SELECTORS.qtyInput).forEach(function (input) {
      if (!resolveIsWholesale(input)) return;
      var raw = input.value;
      var fixed = clamp(raw);
      if (String(fixed) === String(raw)) return;
      input.value = fixed;
      showError(input, 'Wholesale items require a quantity between ' + MIN + ' and ' + MAX + '.');
      input.dispatchEvent(new Event('change', { bubbles: true }));
      blocked = true;
    });
    if (blocked) {
      evt.preventDefault();
      if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
      showToast('One or more wholesale items had an invalid quantity. We\u2019ve corrected it below \u2014 please review and click Check out again.');
    }
    return !blocked;
  }

  // "Add to cart" (product page + sticky bar) — a normal form submit.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    var action = form.getAttribute('action') || '';

    if (/\/cart\/add(\?|$)/.test(action)) {
      guardQuantity(findRelevantQuantityInput(form), e);
      return;
    }

    // "Check out" from the cart page or cart drawer — both post to /cart
    // with a submit button named "checkout".
    if (/^\/cart(\?|$)/.test(action) && form.querySelector('button[name="checkout"], input[name="checkout"]')) {
      guardAllVisibleWholesaleLines(e);
    }
  }, true);

  // "Buy it now" — Shopify's <shopify-accelerated-checkout> web component.
  // Its internal click handling can happen inside a shadow root, so we read
  // the full composed path rather than e.target, which gets retargeted.
  document.addEventListener('click', function (e) {
    var path = (e.composedPath && e.composedPath()) || [e.target];
    var host = null;
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.nodeType === 1 && (
        node.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT' ||
        (node.getAttribute && node.getAttribute('data-shopify') === 'payment-button')
      )) {
        host = node;
        break;
      }
    }
    if (!host) return;
    guardQuantity(findRelevantQuantityInput(host), e);
  }, true);

  document.addEventListener('DOMContentLoaded', function () {
    scan(document);
    scheduleAudit();
  });
})();